import { useState } from 'react';
import { ChevronDown, Search, Pin } from 'lucide-react';
import { navigationGroups, searchMenu, type MenuAudience } from './navigation-model';
export function GroupedNavigation({
  audience,
  currentPage,
  link,
  pins,
  onPinsChange,
}: {
  audience: MenuAudience;
  currentPage: string;
  link: (key: string) => string;
  pins: string[];
  onPinsChange: (pins: string[]) => void;
}) {
  const groups = navigationGroups(audience);
  const pages = groups.flatMap((g) => g.pages);
  const [open, setOpen] = useState(
    groups.find((g) => g.pages.some((p) => p.key === currentPage))?.key ?? groups[0]?.key,
  );
  const [query, setQuery] = useState('');
  const current = pages.find((p) => p.key === currentPage);
  const pinned = pins.includes(currentPage);
  function togglePin() {
    if (!current || (!pinned && pins.length >= 3)) return;
    const next = pinned ? pins.filter((key) => key !== currentPage) : [...pins, currentPage];
    onPinsChange(next);
  }
  const visible = searchMenu(groups, query);
  return (
    <nav className="grouped-navigation" aria-label="เมนูเพิ่มเติม">
      <label className="menu-search">
        <Search aria-hidden="true" />
        <span className="sqi-visually-hidden">ค้นหาเมนู</span>
        <input
          aria-label="ค้นหาเมนู"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="ค้นหาเมนู…"
        />
      </label>
      <button
        className="menu-pin"
        type="button"
        onClick={togglePin}
        disabled={!current || (!pinned && pins.length >= 3)}
        aria-pressed={pinned}
      >
        <Pin aria-hidden="true" />
        {pinned ? 'เลิกปักหมุดหน้านี้' : 'ปักหมุดหน้านี้'} <small>{pins.length}/3</small>
      </button>
      {pins.length > 0 && (
        <div className="menu-shortcuts" aria-label="ทางลัดที่ปักหมุด">
          {pins.map((key) => (
            <a
              className="nav-item"
              key={key}
              href={link(key)}
              aria-current={key === currentPage ? 'page' : undefined}
            >
              {pages.find((p) => p.key === key)?.label}
            </a>
          ))}
        </div>
      )}
      {visible.length === 0 && (
        <p role="status" className="menu-empty">
          ไม่พบเมนู{' '}
          <button type="button" onClick={() => setQuery('')}>
            ล้างคำค้น
          </button>
        </p>
      )}
      {visible.map((group) => {
        const expanded = query.trim() !== '' || open === group.key;
        return (
          <section className="menu-group" key={group.key}>
            <button
              type="button"
              className="menu-group-toggle"
              aria-expanded={expanded}
              onClick={() => {
                setQuery('');
                setOpen(open === group.key ? undefined : group.key);
              }}
            >
              <span>{group.label}</span>
              <ChevronDown aria-hidden="true" />
            </button>
            {expanded && (
              <div className="menu-group-links">
                {group.pages.map((p) => (
                  <a
                    key={p.key}
                    className={`nav-item ${currentPage === p.key ? 'current' : ''}`}
                    href={link(p.key)}
                    aria-current={currentPage === p.key ? 'page' : undefined}
                  >
                    {p.label}
                  </a>
                ))}
              </div>
            )}
          </section>
        );
      })}
    </nav>
  );
}
